import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  parseRepoTicker,
  REPO_SECURITY_TYPE_LABEL,
  repoDealLabel,
  repoDealRouteKey,
  type RepositoryDeal,
} from "@docreview/shared";

type StatusTab = "committed" | "in_progress" | "all";
type ViewMode = "card" | "list";
type SortKey = "ticker_asc" | "ticker_desc";
type TriBool = "" | "true" | "false";

const VIEW_LS_KEY = "repo.deals.view";
const SORT_LS_KEY = "repo.deals.sort";

function readLs<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const v = window.localStorage.getItem(key);
  return (allowed as readonly string[]).includes(v ?? "") ? (v as T) : fallback;
}

export default function RepositoryLibraryPage() {
  const [deals, setDeals] = useState<RepositoryDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<StatusTab>("committed");
  const [showNew, setShowNew] = useState(false);
  const [view, setView] = useState<ViewMode>(() => readLs(VIEW_LS_KEY, ["card", "list"] as const, "card"));
  const [sortBy, setSortBy] = useState<SortKey>(() =>
    readLs(SORT_LS_KEY, ["ticker_asc", "ticker_desc"] as const, "ticker_asc"),
  );
  const [filterSecType, setFilterSecType] = useState("");
  const [filterDaca, setFilterDaca] = useState<TriBool>("");
  const [filterAb, setFilterAb] = useState<TriBool>("");

  useEffect(() => {
    api
      .listRepoDeals()
      .then(({ deals }) => setDeals(deals))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    window.localStorage.setItem(VIEW_LS_KEY, view);
  }, [view]);
  useEffect(() => {
    window.localStorage.setItem(SORT_LS_KEY, sortBy);
  }, [sortBy]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchTri = (tri: TriBool, actual: boolean | null): boolean => {
      if (tri === "") return true;
      if (actual === null) return false;
      return tri === "true" ? actual : !actual;
    };
    const passes = deals.filter((d) => {
      if (tab === "committed" && d.status !== "committed") return false;
      if (tab === "in_progress" && d.status === "committed") return false;
      if (filterSecType && metaString(d, "security_type") !== filterSecType) return false;
      if (filterDaca && !matchTri(filterDaca, metaBoolStrict(d, "has_daca"))) return false;
      if (filterAb && !matchTri(filterAb, metaBoolStrict(d, "ab_note_structure"))) return false;
      if (!q) return true;
      const label = repoDealLabel(d.ticker, d.dealNumber).toLowerCase();
      return label.includes(q) || d.companyName.toLowerCase().includes(q);
    });
    const sorted = [...passes].sort((a, b) => {
      const la = repoDealLabel(a.ticker, a.dealNumber);
      const lb = repoDealLabel(b.ticker, b.dealNumber);
      const cmp = la.localeCompare(lb);
      return sortBy === "ticker_asc" ? cmp : -cmp;
    });
    return sorted;
  }, [deals, query, tab, filterSecType, filterDaca, filterAb, sortBy]);

  const counts = useMemo(
    () => ({
      committed: deals.filter((d) => d.status === "committed").length,
      in_progress: deals.filter((d) => d.status !== "committed").length,
    }),
    [deals],
  );

  const activeFilterCount =
    (filterSecType ? 1 : 0) + (filterDaca ? 1 : 0) + (filterAb ? 1 : 0);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-lg font-semibold">Document Repository</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/repository/ask"
            className="btn-secondary"
            title="Ask a question across every deal in the repository"
          >
            Ask the repository
          </Link>
          <button className="btn-primary" onClick={() => setShowNew(true)}>
            Add deal
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Every closed deal's documents, searchable. Open a deal to ask questions of its
        documents, or use “Ask the repository” for cross-deal questions.
      </p>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input
          className="input max-w-xs"
          placeholder="Search ticker or company…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="flex items-center gap-1 text-sm">
          <TabButton active={tab === "committed"} onClick={() => setTab("committed")}>
            Committed ({counts.committed})
          </TabButton>
          <TabButton active={tab === "in_progress"} onClick={() => setTab("in_progress")}>
            In progress ({counts.in_progress})
          </TabButton>
          <TabButton active={tab === "all"} onClick={() => setTab("all")}>
            All
          </TabButton>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap text-xs">
        <span className="text-slate-400">Filter</span>
        <select
          className="input w-auto text-xs py-1 h-auto"
          value={filterSecType}
          onChange={(e) => setFilterSecType(e.target.value)}
          title="Filter by security type"
        >
          <option value="">Any security type</option>
          {Object.entries(REPO_SECURITY_TYPE_LABEL).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="input w-auto text-xs py-1 h-auto"
          value={filterDaca}
          onChange={(e) => setFilterDaca(e.target.value as TriBool)}
          title="Filter by Deposit Account Control Agreement"
        >
          <option value="">DACA: any</option>
          <option value="true">DACA: yes</option>
          <option value="false">DACA: no</option>
        </select>
        <select
          className="input w-auto text-xs py-1 h-auto"
          value={filterAb}
          onChange={(e) => setFilterAb(e.target.value as TriBool)}
          title="Filter by A/B note structure"
        >
          <option value="">A/B: any</option>
          <option value="true">A/B: yes</option>
          <option value="false">A/B: no</option>
        </select>
        {activeFilterCount > 0 && (
          <button
            className="text-slate-500 hover:text-slate-700 underline"
            onClick={() => {
              setFilterSecType("");
              setFilterDaca("");
              setFilterAb("");
            }}
          >
            Clear ({activeFilterCount})
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <select
            className="input w-auto text-xs py-1 h-auto"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            title="Sort order"
          >
            <option value="ticker_asc">Ticker A → Z</option>
            <option value="ticker_desc">Ticker Z → A</option>
          </select>
          <div className="inline-flex shrink-0 rounded-md border border-slate-200 overflow-hidden">
            <button
              className={`px-2 py-1 ${view === "card" ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100"}`}
              onClick={() => setView("card")}
              title="Card view"
            >
              Cards
            </button>
            <button
              className={`px-2 py-1 ${view === "list" ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100"}`}
              onClick={() => setView("list")}
              title="List view"
            >
              List
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          {deals.length === 0
            ? 'No deals in the repository yet. Click "Add deal" to ingest one.'
            : "No deals match."}
        </div>
      ) : view === "card" ? (
        <ul className="grid gap-2 sm:grid-cols-2">
          {filtered.map((d) => (
            <li key={d.id}>
              <DealCard deal={d} />
            </li>
          ))}
        </ul>
      ) : (
        <DealList deals={filtered} />
      )}

      {showNew && (
        <NewRepoDealModal
          onClose={() => setShowNew(false)}
          onCreated={(d) => setDeals((prev) => [d, ...prev])}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-2.5 py-1 font-medium transition ${
        active ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

function metaString(d: RepositoryDeal, key: string): string | null {
  const v = d.metadata?.[key]?.value;
  return typeof v === "string" && v.length > 0 ? v : null;
}
function metaBool(d: RepositoryDeal, key: string): boolean {
  return d.metadata?.[key]?.value === true;
}
// Strict tri-state read for filter logic: an unextracted / null-valued field
// is neither "yes" nor "no", so it falls out of both directional filters.
// metaBool above stays boolean for badge rendering (badge shows iff true).
function metaBoolStrict(d: RepositoryDeal, key: string): boolean | null {
  const v = d.metadata?.[key]?.value;
  if (v === true) return true;
  if (v === false) return false;
  return null;
}
function metaNum(d: RepositoryDeal, key: string): number | null {
  const v = d.metadata?.[key]?.value;
  return typeof v === "number" ? v : null;
}

function formatUsd(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${n}`;
}

function DealCard({ deal: d }: { deal: RepositoryDeal }) {
  const label = repoDealLabel(d.ticker, d.dealNumber);
  const secType = metaString(d, "security_type");
  const face = metaNum(d, "total_face_value_usd");
  const badges: string[] = [];
  if (secType && REPO_SECURITY_TYPE_LABEL[secType]) badges.push(REPO_SECURITY_TYPE_LABEL[secType]);
  if (metaBool(d, "ab_note_structure")) badges.push("A/B");
  if (metaBool(d, "has_daca")) badges.push("DACA");
  if (metaBool(d, "is_convertible")) badges.push("Convertible");
  if (metaBool(d, "has_warrant")) badges.push("Warrant");

  return (
    <Link
      to={`/repository/deals/${repoDealRouteKey(d)}`}
      className="card p-4 flex flex-col gap-2 hover:border-slate-400 hover:shadow transition h-full"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono font-semibold">{label}</div>
          <div className="text-xs text-slate-500 truncate">
            {d.companyName || <span className="italic text-slate-400">Company pending</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          {d.status !== "committed" && (
            <span className="inline-block rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium px-2 py-0.5 capitalize">
              {d.status.replace("_", " ")}
            </span>
          )}
          {d.status === "committed" && (d.docsAddedAfterCommit?.length ?? 0) > 0 && (
            <span
              className="inline-block rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium px-2 py-0.5"
              title="Documents were added after commit; re-extract to include them"
            >
              new docs
            </span>
          )}
          {face != null && (
            <div className="text-xs text-slate-600 mt-1">{formatUsd(face)}</div>
          )}
        </div>
      </div>
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {badges.map((b) => (
            <span
              key={b}
              className="inline-block rounded bg-slate-100 text-slate-600 text-[10px] font-medium px-1.5 py-0.5"
            >
              {b}
            </span>
          ))}
        </div>
      )}
      <div className="text-[11px] text-slate-400 mt-auto flex items-center justify-between">
        <span>{d.docCount} docs</span>
        {d.closingDate && (
          <span>closed {new Date(d.closingDate).toLocaleDateString(undefined, { timeZone: "UTC" })}</span>
        )}
      </div>
    </Link>
  );
}

function DealList({ deals }: { deals: RepositoryDeal[] }) {
  return (
    <div className="card overflow-hidden">
      <div className="hidden md:grid grid-cols-[100px_minmax(0,1fr)_minmax(0,180px)_100px_110px_60px_110px] gap-3 px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
        <div>Ticker</div>
        <div>Company</div>
        <div>Security Type</div>
        <div className="text-right">Face Value</div>
        <div>Closed</div>
        <div className="text-right">Docs</div>
        <div>Status</div>
      </div>
      <ul className="divide-y divide-slate-100">
        {deals.map((d) => (
          <li key={d.id}>
            <DealRow deal={d} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function DealRow({ deal: d }: { deal: RepositoryDeal }) {
  const label = repoDealLabel(d.ticker, d.dealNumber);
  const secType = metaString(d, "security_type");
  const face = metaNum(d, "total_face_value_usd");
  return (
    <Link
      to={`/repository/deals/${repoDealRouteKey(d)}`}
      className="grid grid-cols-2 gap-x-3 gap-y-1 px-3 py-2 items-center hover:bg-slate-50 transition text-sm md:grid-cols-[100px_minmax(0,1fr)_minmax(0,180px)_100px_110px_60px_110px]"
    >
      <div className="font-mono font-medium truncate">{label}</div>
      <div className="truncate text-slate-700">
        {d.companyName || <span className="italic text-slate-400">Pending</span>}
      </div>
      <div className="truncate text-slate-600 text-xs">
        {(secType && REPO_SECURITY_TYPE_LABEL[secType]) || <span className="text-slate-400">—</span>}
      </div>
      <div className="text-right text-slate-700 text-xs">
        {face != null ? formatUsd(face) : <span className="text-slate-400">—</span>}
      </div>
      <div className="text-slate-500 text-xs">
        {d.closingDate
          ? new Date(d.closingDate).toLocaleDateString(undefined, { timeZone: "UTC" })
          : <span className="text-slate-400">—</span>}
      </div>
      <div className="text-right text-slate-700 text-xs">{d.docCount}</div>
      <div>
        <span
          className={`inline-block rounded-full text-[10px] font-medium px-2 py-0.5 capitalize ${
            d.status === "committed"
              ? "bg-slate-100 text-slate-600"
              : "bg-amber-100 text-amber-700"
          }`}
        >
          {d.status.replace("_", " ")}
        </span>
      </div>
    </Link>
  );
}

function NewRepoDealModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (d: RepositoryDeal) => void;
}) {
  const nav = useNavigate();
  const [label, setLabel] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [lenderEntity, setLenderEntity] = useState("");
  const [entities, setEntities] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listRepoEntities().then(({ entities }) => setEntities(entities.map((e) => e.name)));
  }, []);

  const addEntityInline = async () => {
    const name = window.prompt("New lender entity (exact legal name):")?.trim();
    if (!name) return;
    const { entity } = await api.addRepoEntity(name);
    setEntities((prev) => (prev.includes(entity.name) ? prev : [...prev, entity.name].sort()));
    setLenderEntity(entity.name);
  };

  const parsed = label.trim() ? parseRepoTicker(label) : null;
  // Company name is optional now — extraction auto-fills it from the documents.
  const canSubmit = label.trim().length > 0 && !busy;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-20">
      <div className="card w-full max-w-md p-6">
        <h2 className="font-semibold text-lg mb-1">Add deal to repository</h2>
        <p className="text-xs text-slate-500 mb-4">
          You'll upload and tag documents on the next screen.
        </p>

        <label className="block text-sm font-medium mb-1">Deal label</label>
        <input
          className="input mb-1 font-mono"
          placeholder="AAPL2"
          value={label}
          // Letters + digits only — the label goes straight into the deal URL,
          // so it must stay URL-safe (the backend enforces this too).
          onChange={(e) => setLabel(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
          autoFocus
        />
        {parsed && (
          <div className="text-xs text-slate-400 mb-3">
            Ticker <span className="font-mono">{parsed.ticker}</span> · deal #{parsed.dealNumber}
            {parsed.dealNumber === 1 && " (first deal with this company)"}
          </div>
        )}

        <label className="block text-sm font-medium mb-1">
          Company name <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <input
          className="input mb-1"
          placeholder="Leave blank to auto-fill from the documents"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
        <p className="text-[11px] text-slate-400 mb-3">
          Extraction pulls the company name from the documents — you can leave this blank.
        </p>

        <label className="block text-sm font-medium mb-1">
          Lender entity <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <div className="flex gap-2 mb-1">
          <select
            className="input"
            value={lenderEntity}
            onChange={(e) => setLenderEntity(e.target.value)}
          >
            <option value="">— select —</option>
            {entities.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <button type="button" className="btn-secondary whitespace-nowrap" onClick={addEntityInline}>
            + Add
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mb-4">
          Which of our entities was the lender on this deal.
        </p>

        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!canSubmit}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const { deal } = await api.createRepoDeal({
                  ticker: label.trim(),
                  companyName: companyName.trim() || undefined,
                  lenderEntity: lenderEntity.trim() || undefined,
                });
                onCreated(deal);
                nav(`/repository/deals/${repoDealRouteKey(deal)}`);
              } catch (e) {
                const msg = e instanceof Error ? e.message : String(e);
                // Surface the backend's friendly message when present.
                setError(msg.replace(/^\d+\s+/, "").replace(/^\{.*"message":"([^"]+)".*\}$/, "$1"));
                setBusy(false);
              }
            }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
